import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type RefObject,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useHoverTip } from './hoverTip';
import { Button } from './ui';
import { cn } from '../utils/cn';

// =============================================================================
// backdropCloseProps — 딤(백드롭) 클릭 닫기 공용 핸들러.
// 인풋 텍스트를 드래그하다 딤 위에서 릴리즈하면 click이 딤에서 발화해 모달이
// 닫혀버리는 문제 방지: mousedown과 click(=mouseup) 둘 다 딤 자체에서 시작·종료
// 됐을 때만 닫는다. 상태는 DOM dataset에 둬서 훅 규칙과 무관 — 백드롭 요소에
// {...backdropCloseProps(onClose)} 로 스프레드.
// =============================================================================
export function backdropCloseProps(onClose: () => void): {
  onMouseDown: (e: ReactMouseEvent<HTMLElement>) => void;
  onClick: (e: ReactMouseEvent<HTMLElement>) => void;
} {
  return {
    onMouseDown: (e) => {
      if (e.target === e.currentTarget) e.currentTarget.dataset['downOnBackdrop'] = '1';
      else delete e.currentTarget.dataset['downOnBackdrop'];
    },
    onClick: (e) => {
      const ok = e.currentTarget.dataset['downOnBackdrop'] === '1';
      delete e.currentTarget.dataset['downOnBackdrop'];
      if (ok && e.target === e.currentTarget) onClose();
    },
  };
}
import { describeApiError } from './toast';

// =============================================================================
// 공용 팝오버 메뉴 / 미니 모달 (미디어 관리 화면에서 정립된 UX 패턴).
//   - ToolbarMenu : ⋮/＋/≡ 아이콘 팝오버. body 포털 + fixed 배치 → overflow에
//     잘리지 않고, 바깥클릭/Esc/스크롤 시 닫힘.
//   - MenuRow     : 메뉴 항목 버튼(라벨 + 선택 체크).
//   - InfoTooltip : ⓘ hover 말풍선.
//   - ModalShell  : 미니 모달 셸(제목/닫기, Esc·바깥클릭 닫기).
//   - ConfirmModal / TextPromptModal : window.confirm / window.prompt 대체.
// =============================================================================

// 헤더 + / 정렬 / 더보기(⋮) 아이콘 버튼 팝오버.
// 팝오버는 body 포털 + position:fixed로 렌더 → 타일의 overflow-hidden이나
// 스크롤 컨테이너에 잘리지 않는다. 트리거 rect 기준으로 배치하며 아래 공간이
// 부족하면 위로 뒤집는다. 바깥 클릭 / Esc / 스크롤 시 닫힌다.
/** 도구줄의 단독 실행 버튼 — 메뉴 없이 곧바로 실행한다. ToolbarMenu 의 방아쇠와 크기·테두리가
 *  같아서 '메뉴가 열리느냐 아니냐'만 다르고 자리는 흔들리지 않는다(2026-09-17 대교: 고를 것이
 *  하나뿐인 메뉴는 한 단계를 헛되이 만든다). */
export function ToolbarButton({ glyph, title, disabled = false, className, onClick }: {
  glyph: ReactNode;
  title: string;
  disabled?: boolean;
  className?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      disabled={disabled}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClick(); }}
      className={cn(
        'cursor-pointer select-none inline-flex h-7 w-7 items-center justify-center rounded-sm border border-line bg-panel text-[15px] hover:border-line-strong disabled:cursor-not-allowed disabled:opacity-40',
        className,
      )}
    >
      {glyph}
    </button>
  );
}

export function ToolbarMenu({
  glyph,
  title,
  align = 'left',
  side = 'auto',
  disabled = false,
  className,
  children,
}: {
  glyph: ReactNode;
  title: string;
  align?: 'left' | 'right';
  side?: 'auto' | 'top';
  disabled?: boolean;
  className?: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left?: number; right?: number }>({ top: 0 });

  const place = () => {
    const b = btnRef.current?.getBoundingClientRect();
    if (!b) return;
    const menuH = menuRef.current?.offsetHeight ?? 0;
    const below = b.bottom + 4;
    const openUp = side === 'top' || menuH > 0 && below + menuH > window.innerHeight && b.top - menuH - 4 > 0;
    const top = openUp ? Math.max(8, b.top - menuH - 8) : below;
    setPos(
      align === 'right'
        ? { top, right: Math.max(4, window.innerWidth - b.right) }
        : { top, left: b.left },
    );
  };

  // 열릴 때 위치 계산 + 높이 측정 후 재배치(위/아래 뒤집기). 바깥 클릭·Esc·
  // 스크롤·리사이즈 시 닫기. useLayoutEffect로 첫 페인트 전 자리 잡아 깜빡임 최소화.
  useLayoutEffect(() => {
    if (!open) return;
    place();
    const raf = requestAnimationFrame(() => { place(); if (side === 'top') menuRef.current?.querySelector<HTMLButtonElement>('button')?.focus(); });
    const onDown = (e: MouseEvent) => {
      if (btnRef.current?.contains(e.target as Node)) return;
      if (menuRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { if (side === 'top') { e.preventDefault(); e.stopPropagation(); btnRef.current?.focus(); } setOpen(false); }
      else if (side === 'top' && ['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) {
        const items = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)') ?? []);
        if (!items.length) return;
        e.preventDefault(); e.stopPropagation();
        const index = items.indexOf(document.activeElement as HTMLButtonElement);
        const next = e.key === 'Home' ? 0 : e.key === 'End' ? items.length - 1 : (index + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
        items[next]?.focus();
      }
    };
    const onScrollResize = () => setOpen(false);
    window.addEventListener('mousedown', onDown, true);
    window.addEventListener('keydown', onKey, side === 'top');
    window.addEventListener('scroll', onScrollResize, true);
    window.addEventListener('resize', onScrollResize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('keydown', onKey, side === 'top');
      window.removeEventListener('scroll', onScrollResize, true);
      window.removeEventListener('resize', onScrollResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, align, side]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        title={title}
        aria-label={title}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className={cn(
          'cursor-pointer select-none inline-flex h-7 w-7 items-center justify-center rounded-sm border border-line bg-panel text-[15px] hover:border-line-strong',
          className,
        )}
      >
        {glyph}
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            aria-label={title}
            style={{ position: 'fixed', top: pos.top, left: pos.left, right: pos.right, zIndex: 100 }}
            className="min-w-[160px] max-h-[70vh] overflow-auto rounded-md border border-line bg-panel shadow-lg p-1 flex flex-col gap-0.5"
            onClick={() => setOpen(false)}
          >
            {children}
          </div>,
          document.body,
        )}
    </>
  );
}

/** 메뉴 안 구분선 — 삭제처럼 성격이 다른 항목 앞에. */
export function MenuDivider(): JSX.Element {
  return <div role="separator" className="my-0.5 border-t border-line" />;
}

/** 탭 한 벌 — role=tablist/tab/aria-selected를 빠뜨리지 않게 묶어 둔다(검수 C4). */
export function TabBar<V extends string>({
  label,
  value,
  options,
  onChange,
  className,
  tabClassName,
}: {
  label: string;
  value: V;
  options: ReadonlyArray<{ value: V; label: ReactNode; title?: string }>;
  onChange: (next: V) => void;
  className?: string;
  tabClassName?: string;
}): JSX.Element {
  return (
    <div role="tablist" aria-label={label} className={cn('flex items-center gap-1', className)}>
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={active}
            title={opt.title}
            onClick={() => onChange(opt.value)}
            className={cn(
              'rounded-sm border px-2.5 py-1 text-[13px] transition-colors duration-fast',
              active
                ? 'border-accent-ring bg-accent-soft text-accent'
                : 'border-line bg-transparent text-text-dim hover:text-text hover:border-line-strong',
              tabClassName,
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

export function MenuRow({
  label,
  onClick,
  checked,
  danger = false,
  disabled = false,
  title,
}: {
  label: string;
  onClick: () => void;
  checked?: boolean;
  danger?: boolean;
  /** 조건이 안 맞아 지금은 못 하는 항목 — 숨기지 않고 비활성으로 두고 title로 이유를 알린다. */
  disabled?: boolean;
  title?: string;
}) {
  // 비활성 사유는 기본 title 대신 aria-description — HoverTipLayer가 말풍선으로 띄운다(2026-10-01 대교).
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      disabled={disabled}
      {...(disabled && title ? { 'aria-description': title } : { title })}
      className={cn(
        'flex items-center justify-between gap-3 px-2.5 py-1.5 text-left text-[13px] rounded-sm hover:bg-panel-2 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent',
        danger && 'text-red hover:bg-red/10',
      )}
    >
      <span>{label}</span>
      {checked && <span className="text-accent">✓</span>}
    </button>
  );
}

// ⓘ 아이콘 + 호버 말풍선. 말풍선을 body로 포탈해 fixed로 띄운다 → 조상의
// overflow-hidden/auto에 잘리지 않는다(기존 absolute 방식은 스크롤 패널에 잘림).
export function InfoTooltip({ text }: { text: string }) {
  const { props, tip } = useHoverTip(text);
  return (
    <span className="relative inline-flex align-middle">
      <span tabIndex={0} role="img" aria-label={text} {...props} className="cursor-help text-text-dim leading-none outline-none">
        ⓘ
      </span>
      {tip}
    </span>
  );
}

// 모달 폭 3단계 (2026-09-16 검수 C5 — 400/440/560/920/1040/1120 6종을 정리).
//   sm 400 = 확인·짧은 폼 / md 560 = 일반 폼 / lg 920 = 표·목록을 품는 모달
export type ModalSize = 'sm' | 'md' | 'lg';
const MODAL_WIDTH: Record<ModalSize, string> = {
  sm: 'w-[400px]',
  md: 'w-[560px]',
  lg: 'w-[920px]',
};

const FOCUSABLE =
  'a[href],button:not(:disabled),input:not(:disabled),select:not(:disabled),textarea:not(:disabled),[tabindex]:not([tabindex="-1"])';

// =============================================================================
// useDialogPanel — 모달 패널이 지켜야 할 포커스 동작을 한 벌로 (검수 S9·S10).
//   열 때 첫 입력(없으면 첫 조작부)으로 포커스 / 닫을 때 열었던 요소로 복귀 /
//   Tab이 모달 밖으로 새지 않게 앞뒤 순환.
// ModalShell이 이걸 쓰고, 자체 셸을 가진 모달(URL 추가·미디어 선택 등)도 같은 걸 쓴다.
// 반환값을 패널 요소에 스프레드: <div {...useDialogPanel()} role="dialog" aria-modal="true">
// =============================================================================
export function useDialogPanel<T extends HTMLElement = HTMLDivElement>(): {
  ref: RefObject<T>;
  tabIndex: -1;
  onKeyDown: (e: ReactKeyboardEvent<T>) => void;
} {
  const ref = useRef<T>(null);

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const panel = ref.current;
    const raf = requestAnimationFrame(() => {
      if (!panel || panel.contains(document.activeElement)) return;
      const first = panel.querySelector<HTMLElement>(
        'input:not(:disabled):not([type="hidden"]),select:not(:disabled),textarea:not(:disabled)',
      ) ?? panel.querySelector<HTMLElement>(FOCUSABLE);
      (first ?? panel).focus();
    });
    return () => {
      cancelAnimationFrame(raf);
      if (opener && document.contains(opener)) opener.focus();
    };
  }, []);

  const onKeyDown = (e: ReactKeyboardEvent<T>): void => {
    if (e.key !== 'Tab') return;
    const panel = ref.current;
    if (!panel) return;
    // 숨겨진 항목은 뺀다. offsetParent로 판정하면 레이아웃이 없는 환경(jsdom)에서
    // 전부 null이라 목록이 비고 트랩이 통째로 죽는다 — 속성으로 판정한다.
    const items = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
      (el) => el === document.activeElement || !el.closest('[hidden],[aria-hidden="true"]'),
    );
    if (!items.length) return;
    const first = items[0]!;
    const last = items[items.length - 1]!;
    const active = document.activeElement as HTMLElement | null;
    if (e.shiftKey && (active === first || active === panel)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return { ref, tabIndex: -1, onKeyDown };
}

// =============================================================================
// ModalShell — 앱의 모든 미니 모달 껍데기 (2026-09-16 검수 P2로 재정비).
//
//   header(고정) / body(스크롤) / footer(고정)  3분할.
//   • 제출·취소 버튼은 `footer`로 넘긴다 → 내용이 길어도 화면 밖으로 밀리지 않는다.
//     (검수 S3: 1280×800에서 그룹 수정·관리자 추가·디바이스 수정 푸터가 잘렸다)
//   • role="dialog" + aria-modal + aria-labelledby, Tab 트랩, 열 때 첫 입력으로
//     포커스, 닫을 때 열었던 요소로 복귀 (검수 S9·S10).
//   • 높이는 min(720px, 100vh - 80px)로 클램프. 폭은 size 3단계.
// 원칙("조작부는 움직이지 않는다"): 내용이 바뀌어도 푸터 자리는 그대로다.
// =============================================================================
export function ModalShell({
  title,
  onClose,
  children,
  footer,
  size = 'sm',
  panelClassName,
  bodyClassName,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** 제출·취소 줄. 주면 스크롤 밖 고정 푸터로 붙는다. */
  footer?: ReactNode;
  size?: ModalSize;
  /** Optional per-dialog sizing/layout. 주면 size 기본 폭을 대체한다. */
  panelClassName?: string;
  bodyClassName?: string;
}) {
  const panel = useDialogPanel<HTMLDivElement>();
  const titleId = useId();
  const hasOwnHeight = /(?:^|\s)(?:max-)?h-\[/.test(panelClassName ?? '');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      // 모바일에서는 globals.css 가 이 표시를 보고 작은 창은 아래 시트로, 나머지는 전체 화면으로 편다.
      data-modal-backdrop={size === 'sm' && !panelClassName ? 'sheet' : 'full'}
      className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-8"
      {...backdropCloseProps(onClose)}
    >
      <div
        {...panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={cn(
          'max-w-full bg-panel border border-line rounded-md shadow-lg flex flex-col outline-none',
          // cn은 단순 join이라 같은 속성 클래스가 겹치면 순서가 보장되지 않는다.
          // 모달이 스스로 높이를 정했으면(템플릿 갤러리 등 고정 높이) 기본 클램프를 빼고,
          // 아니면 min(720px, 100vh-80px)로 묶어 1280×800에서도 푸터가 살아 있게 한다.
          hasOwnHeight ? '' : 'max-h-[min(720px,100vh-80px)]',
          panelClassName ?? MODAL_WIDTH[size],
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex flex-none items-center justify-between px-5 pt-5 pb-3">
          <h2 id={titleId} className="text-[16px] font-semibold">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            title="닫기"
            aria-label="닫기"
            data-modal-close
            className="flex h-7 w-7 items-center justify-center rounded-sm text-text-dim hover:bg-panel-2 hover:text-text"
          >
            ✕
          </button>
        </header>
        <div className={cn('flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-5 pb-5', bodyClassName)}>
          {children}
        </div>
        {footer && (
          <div className="flex flex-none items-center justify-end gap-2 border-t border-line bg-panel px-5 py-3.5">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

// window.confirm 대체 — 제목/메시지 + 취소·확인. 확인은 기본 danger(삭제류).
export function ConfirmModal({
  title,
  message,
  confirmLabel = '삭제',
  danger = true,
  hideCancel = false,
  confirmDisabled = false,
  confirmDisabledReason,
  onCancel,
  onConfirm,
}: {
  title: string;
  message?: ReactNode;
  confirmLabel?: string;
  /** 지금은 확정할 수 없다 — 버튼을 없애지 않고 비활성 + 이유로 남긴다(조작부는 움직이지 않는다). */
  confirmDisabled?: boolean;
  confirmDisabledReason?: string | undefined;
  danger?: boolean;
  /** 알림(확인만) 모달 — 취소 버튼 숨김. */
  hideCancel?: boolean;
  onCancel: () => void;
  onConfirm: () => void | Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
      onCancel();
    } catch (e) {
      // 권한 없음 등 실패를 조용히 삼키지 않고 모달 안에 원인을 보여준다.
      setError(describeApiError(e));
      setBusy(false);
    }
  };
  return (
    <ModalShell
      title={title}
      onClose={onCancel}
      footer={
        <>
          {!hideCancel && (
            <Button onClick={onCancel} disabled={busy}>
              취소
            </Button>
          )}
          <Button variant={danger ? 'danger' : 'primary'} className="min-w-[88px] justify-center"
            onClick={() => void run()} disabled={busy || confirmDisabled}
            {...(confirmDisabled && confirmDisabledReason ? { title: confirmDisabledReason } : {})}>
            {busy ? '처리 중…' : confirmLabel}
          </Button>
        </>
      }
    >
      {/* 문단만 오는 게 아니라 사용처 목록 같은 블록도 들어온다 — p 가 아니라 div 로 감싼다. */}
      {message && (
        <div className="text-[14px] text-text-dim leading-relaxed whitespace-pre-line">{message}</div>
      )}
      {error && <div className="font-mono text-[12px] text-red">{error}</div>}
    </ModalShell>
  );
}

// window.prompt 대체 — 단일 텍스트 입력. 빈 값이면 확인 비활성.
export function TextPromptModal({
  title,
  label,
  initialValue = '',
  placeholder,
  submitLabel = '저장',
  onClose,
  onSubmit,
}: {
  title: string;
  label: string;
  initialValue?: string;
  placeholder?: string;
  submitLabel?: string;
  onClose: () => void;
  onSubmit: (value: string) => void | Promise<void>;
}) {
  const [value, setValue] = useState(initialValue);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async () => {
    const trimmed = value.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onSubmit(trimmed);
      onClose();
    } catch (e) {
      setError(describeApiError(e));
      setBusy(false);
    }
  };
  return (
    <ModalShell
      title={title}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            취소
          </Button>
          <Button variant="primary" className="min-w-[88px] justify-center" disabled={!value.trim() || busy} onClick={() => void submit()}>
            {busy ? '저장 중…' : submitLabel}
          </Button>
        </>
      }
    >
      <label className="block">
        <span className="block font-mono text-[12px] font-medium uppercase tracking-monoWide text-text-dim mb-1.5">
          {label}
        </span>
        <input
          autoFocus
          value={value}
          placeholder={placeholder}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void submit();
          }}
          className="w-full px-2.5 py-2 bg-panel-2 border border-line rounded text-[15px] text-text focus:outline-none focus:border-accent"
        />
      </label>
      {error && <div className="font-mono text-[12px] text-red">{error}</div>}
    </ModalShell>
  );
}
